<template>
  <section class="px-3 pb-3 admin-users-page">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">Пользователи</span>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadUsers">
          <i class="bi bi-arrow-repeat me-1"></i>
          Обновить
        </button>
      </div>

      <div class="admin-filters row g-2">
        <div class="col-12 col-lg-5">
          <input
            v-model.trim="searchQuery"
            type="search"
            class="form-control form-control-sm"
            placeholder="Поиск по id, логину, ролям"
          />
        </div>
        <div class="col-6 col-lg-3">
          <select v-model="roleFilter" class="form-select form-select-sm">
            <option value="">Все роли</option>
            <option v-for="role in ROLE_ENUM_VALUES" :key="role" :value="role">{{ role }}</option>
          </select>
        </div>
        <div class="col-6 col-lg-2">
          <select v-model="statusFilter" class="form-select form-select-sm">
            <option value="">Все статусы</option>
            <option value="enabled">Активные</option>
            <option value="disabled">Отключенные</option>
          </select>
        </div>
        <div class="col-12 col-lg-2 text-lg-end text-muted small d-flex align-items-center justify-content-lg-end">
          Найдено: {{ filteredUsers.length }}
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div class="row g-3 mt-1">
        <div class="col-12 col-xl-7">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Пользователь</th>
                  <th>Роли</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody v-if="filteredUsers.length">
                <tr
                  v-for="user in filteredUsers"
                  :key="user.id"
                  class="admin-table__row"
                  :class="{ 'admin-table__row--active': selectedUserId === user.id }"
                  @click="selectUser(user)"
                >
                  <td>{{ user.id }}</td>
                  <td>{{ user.username || '—' }}</td>
                  <td>
                    <span class="badge bg-light text-dark border me-1" v-for="role in user.roles" :key="`${user.id}-${role}`">
                      {{ role }}
                    </span>
                    <span v-if="!user.roles.length" class="text-muted">—</span>
                  </td>
                  <td>
                    <span :class="user.enabled ? 'text-success' : 'text-danger'">
                      {{ user.enabled ? 'Активен' : 'Отключен' }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="4" class="text-center text-muted py-3">Пользователи не найдены.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-12 col-xl-5">
          <div class="admin-detail" v-if="selectedUser">
            <div class="admin-detail__title">Пользователь #{{ selectedUser.id }}</div>
            <div class="admin-detail__subtitle mb-2">{{ selectedUser.username || 'Без имени пользователя' }}</div>

            <form class="admin-form-block" @submit.prevent="submitStatusUpdate">
              <div class="admin-form-block__title">Статусы аккаунта</div>
              <div class="form-check mb-1">
                <input id="user-enabled" v-model="statusForm.enabled" class="form-check-input" type="checkbox" />
                <label for="user-enabled" class="form-check-label">Активен (enabled)</label>
              </div>
              <div class="form-check mb-1">
                <input id="user-account-non-expired" v-model="statusForm.accountNonExpired" class="form-check-input" type="checkbox" />
                <label for="user-account-non-expired" class="form-check-label">Не просрочен (accountNonExpired)</label>
              </div>
              <div class="form-check mb-1">
                <input
                  id="user-credentials-non-expired"
                  v-model="statusForm.credentialsNonExpired"
                  class="form-check-input"
                  type="checkbox"
                />
                <label for="user-credentials-non-expired" class="form-check-label">Учетные данные актуальны (credentialsNonExpired)</label>
              </div>
              <div class="form-check mb-2">
                <input id="user-account-non-locked" v-model="statusForm.accountNonLocked" class="form-check-input" type="checkbox" />
                <label for="user-account-non-locked" class="form-check-label">Не заблокирован (accountNonLocked)</label>
              </div>
              <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">Сохранить статусы</button>
            </form>

            <form class="admin-form-block" @submit.prevent="submitRolesUpdate">
              <div class="admin-form-block__title">Роли пользователя</div>
              <div class="admin-role-list">
                <div class="form-check" v-for="role in ROLE_ENUM_VALUES" :key="role">
                  <input
                    :id="`role-${role}`"
                    :checked="rolesForm.includes(role)"
                    class="form-check-input"
                    type="checkbox"
                    @change="toggleRole(role)"
                  />
                  <label class="form-check-label" :for="`role-${role}`">{{ role }}</label>
                </div>
              </div>
              <button type="submit" class="btn btn-primary btn-sm mt-2" :disabled="isLoading">Сохранить роли</button>
            </form>

            <form class="admin-form-block" @submit.prevent="submitPasswordReset">
              <div class="admin-form-block__title">Сброс пароля</div>
              <label class="form-label mb-1">Новый пароль</label>
              <input
                v-model="passwordForm.newPassword"
                type="text"
                class="form-control form-control-sm"
                placeholder="Введите новый пароль"
                autocomplete="new-password"
              />
              <button type="submit" class="btn btn-outline-danger btn-sm mt-2" :disabled="isLoading">Сбросить пароль</button>
            </form>
          </div>

          <div v-else class="admin-empty-state">
            Выберите пользователя из таблицы.
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import {
  ROLE_ENUM_VALUES,
  validateAdminUserPasswordResetRequest,
  validateAdminUserRolesUpdateRequest,
} from '@/admin/types/adminContracts'
import {
  getAdminUserById,
  getAdminUsers,
  resetAdminUserPassword,
  updateAdminUserRoles,
  updateAdminUserStatus,
} from '@/admin/api/adminService'
import { normalizeSearchText } from '@/admin/utils/adminApiUtils'

const users = ref([])
const selectedUser = ref(null)
const selectedUserId = ref(null)

const searchQuery = ref('')
const roleFilter = ref('')
const statusFilter = ref('')

const statusForm = reactive({
  enabled: false,
  accountNonExpired: false,
  credentialsNonExpired: false,
  accountNonLocked: false,
})

const rolesForm = ref([])
const passwordForm = reactive({
  newPassword: '',
})

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  execute,
} = useAdminRequestState()

const filteredUsers = computed(() => {
  const query = normalizeSearchText(searchQuery.value)
  return users.value.filter(user => {
    if (roleFilter.value && !user.roles.includes(roleFilter.value)) return false
    if (statusFilter.value === 'enabled' && !user.enabled) return false
    if (statusFilter.value === 'disabled' && user.enabled) return false
    if (!query) return true

    const haystack = `${user.id} ${user.username} ${user.roles.join(' ')}`.toLowerCase()
    return haystack.includes(query)
  })
})

const syncFormsWithSelectedUser = user => {
  if (!user) return
  statusForm.enabled = Boolean(user.enabled)
  statusForm.accountNonExpired = Boolean(user.accountNonExpired)
  statusForm.credentialsNonExpired = Boolean(user.credentialsNonExpired)
  statusForm.accountNonLocked = Boolean(user.accountNonLocked)
  rolesForm.value = [...user.roles]
  passwordForm.newPassword = ''
}

watch(selectedUser, syncFormsWithSelectedUser, { immediate: true })

const syncUserInList = updatedUser => {
  users.value = users.value.map(item => (item.id === updatedUser.id ? updatedUser : item))
  if (selectedUserId.value === updatedUser.id) selectedUser.value = updatedUser
}

const loadUserDetails = async userId => {
  const user = await execute(() => getAdminUserById(userId), {
    error: 'Не удалось загрузить карточку пользователя.',
  })
  selectedUser.value = user
}

const loadUsers = async () => {
  clearMessages()
  const list = await execute(() => getAdminUsers(), {
    error: 'Не удалось получить список пользователей.',
  })
  users.value = list

  if (!list.length) {
    selectedUserId.value = null
    selectedUser.value = null
    return
  }

  if (!selectedUserId.value || !list.some(user => user.id === selectedUserId.value)) {
    selectedUserId.value = list[0].id
  }

  await loadUserDetails(selectedUserId.value)
}

const selectUser = async user => {
  if (!user?.id || selectedUserId.value === user.id) return
  selectedUserId.value = user.id
  clearMessages()
  await loadUserDetails(user.id)
}

const submitStatusUpdate = async () => {
  if (!selectedUserId.value) return
  clearMessages()
  const updated = await execute(
    () =>
      updateAdminUserStatus(selectedUserId.value, {
        enabled: statusForm.enabled,
        accountNonExpired: statusForm.accountNonExpired,
        credentialsNonExpired: statusForm.credentialsNonExpired,
        accountNonLocked: statusForm.accountNonLocked,
      }),
    {
      success: 'Статусы пользователя обновлены.',
      error: 'Не удалось обновить статусы пользователя.',
    }
  )
  syncUserInList(updated)
}

const toggleRole = role => {
  if (!ROLE_ENUM_VALUES.includes(role)) return
  if (rolesForm.value.includes(role)) {
    rolesForm.value = rolesForm.value.filter(item => item !== role)
  } else {
    rolesForm.value = [...rolesForm.value, role]
  }
}

const submitRolesUpdate = async () => {
  if (!selectedUserId.value) return
  clearMessages()

  const validationErrors = validateAdminUserRolesUpdateRequest({ roles: rolesForm.value })
  if (validationErrors.length) {
    pushError(validationErrors[0])
    return
  }

  const updated = await execute(
    () =>
      updateAdminUserRoles(selectedUserId.value, {
        roles: rolesForm.value,
      }),
    {
      success: 'Роли пользователя обновлены.',
      error: 'Не удалось обновить роли пользователя.',
    }
  )

  syncUserInList(updated)
}

const submitPasswordReset = async () => {
  if (!selectedUserId.value) return
  clearMessages()

  const validationErrors = validateAdminUserPasswordResetRequest(passwordForm)
  if (validationErrors.length) {
    pushError(validationErrors[0])
    return
  }

  const confirmed = window.confirm('Сбросить пароль этого пользователя?')
  if (!confirmed) return

  await execute(
    () =>
      resetAdminUserPassword(selectedUserId.value, {
        newPassword: passwordForm.newPassword,
      }),
    {
      success: 'Пароль пользователя сброшен.',
      error: 'Не удалось сбросить пароль пользователя.',
    }
  )

  passwordForm.newPassword = ''
}

onMounted(async () => {
  await loadUsers()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-filters {
  margin-top: 10px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-table__row {
  cursor: pointer;
}

.admin-table__row td {
  color: #2b3f61;
}

.admin-table__row--active td {
  background: #eef5ff;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}

.admin-detail__subtitle {
  color: #5f7393;
  font-size: 13px;
}

.admin-form-block + .admin-form-block {
  margin-top: 10px;
}

.admin-form-block {
  border: 1px solid #e0e9f8;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
}

.admin-form-block__title {
  color: #334d74;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.admin-role-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(140px, 1fr));
  gap: 4px 8px;
}

.admin-empty-state {
  border: 1px dashed #c9d8f3;
  border-radius: 12px;
  background: #f8fbff;
  padding: 14px;
  color: #60789d;
}
</style>
